import { html, TemplateResult, LitElement } from 'lit';
import { customElement, property, queryAssignedElements } from 'lit/decorators.js';
import globalStyles from './styles/outline-teaser-card.global.pcss?inline';
import encapsulatedStyles from './styles/outline-teaser-card.encapsulated.pcss?inline';
import { AdoptedStyleSheets } from '../../../controllers/adopted-stylesheets';
import { screenSizeMap } from '../../../controllers/screen-size-controller';
import { validateSlot } from '../../../utilities/slot';


export const teaserImageVariants = ['news', 'event', 'unset'] as const;
export type TeaserImageVariant = (typeof teaserImageVariants)[number];


/**
 * The element name, reused throughout the codebase
 * @type {string}
 */
const componentName = 'outline-teaser-card';

/**
 * The Outline Teaser Card component
 *
 * A reusable LitElement component for displaying teaser-cards.
 *
 * @element outline-teaser-card
 * @extends LitElement
 * @slot image - An outline image compoenent
 * @slot heading - An outline heading component
 * @slot date - An outline heading component
 * @slot descriptions - A outline ckeditor component
 * @slot tags - Basic <a> tags
 */
@customElement(componentName)
export class OutlineTeaserCard extends LitElement {
  resizeObserver: ResizeObserver;

  /**
   * Adopted stylesheets for the component
   *
   * @type {AdoptedStyleSheets}
   */
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
    encapsulatedCSS: encapsulatedStyles,
  });
  /**
   * Ref to elements in the `eyebrow` slot
   */
  @queryAssignedElements({slot: 'eyebrow'})
  eyebrowRef: Array<HTMLElement>;
  
  
  /**
   * Ref to elements in the `heading` slot
   */
  @queryAssignedElements({slot: 'heading'})
  headingRef: Array<HTMLElement>;
  
  /**
   * Ref to elements in the `image` slot
   */
  @queryAssignedElements({slot: 'image'})
  imageRef: Array<HTMLElement>;

  /**
   * Ref to elements in the `date` slot
   */
  @queryAssignedElements({slot: 'date'})
  dateRef: Array<HTMLElement>;

  /**
   * Ref to elements in the `description` slot
   */
  @queryAssignedElements({slot: 'description' })
  descriptionRef: Array<HTMLElement>;

  /**
   * Ref to elements in the `tags` slot
   */
  @queryAssignedElements({slot: 'tags' })
  tagsRef: Array<HTMLElement>;

  /**
   * If screen is below mobile width
   */
  @property({ type: Boolean, reflect: true })
  isMobile: boolean;

  /**
   * Sets the image ratio depending on card variant, or unset for no set image ratio
   */
  @property({ type: String, reflect: true })
  variant: TeaserImageVariant;

  /**
   * Ref to this
   */
  _selfRef: HTMLElement;

  /**
   * Value of the href attribute on <a> if sloted in the `heading` slot.
   * Passed to this.clickHandler.
   */
  _linkHref: string;

  connectedCallback() {
    super.connectedCallback();
    this.resizeObserver = new ResizeObserver(() => {
      this.resizeAtScreen();
    });
  };

  firstUpdated() {
    this.setSelfRef();
    // this.getHref();
    validateSlot(this.eyebrowRef, '.outline-teaser-card--eyebrow', this.shadowRoot);
    validateSlot(this.imageRef, '.outline-teaser-card--image', this.shadowRoot);
    validateSlot(this.dateRef, '.outline-teaser-card--date', this.shadowRoot);
    validateSlot(this.descriptionRef, '.outline-teaser-card--description', this.shadowRoot);
    validateSlot(this.tagsRef, '.outline-teaser-card--tags', this.shadowRoot);
    this.resizeObserver.observe(this._selfRef);
  }

  resizeAtScreen() {
    this.isMobile = this._selfRef.clientWidth < (screenSizeMap.get('xs') as number);
  }

  setSelfRef() {
    const self = this.shadowRoot?.querySelector('.outline-teaser-card') as HTMLElement;
    if (self) {      
      this._selfRef = self;      
    }
  }
  render(): TemplateResult {
    return html`
      <section class="outline-teaser-card">
        <div class="outline-teaser-card--wrapper">
          ${this.isMobile ? this.imageTemplate() : null}
          <div class="outline-teaser-card--content">
            <div class="outline-teaser-card--eyebrow">
              <slot name="eyebrow"></slot>
            </div>
            <div class="outline-teaser-card--heading">
              <slot name="heading"></slot>
            </div>
            <div class="outline-teaser-card--date">
              <slot name="date"></slot>
            </div>
            <div class="outline-teaser-card--description">
              <slot name="description"></slot>
            </div>
            <div class="outline-teaser-card--tags">
              <slot name="tags"></slot>
            </div>
          </div>
          ${!this.isMobile ? this.imageTemplate() : null}
        </div>
      </section>
    `;
  }

  imageTemplate() {
    return html `
      <div class="outline-teaser-card--image">
        <slot name="image"></slot>
      </div>
    `
  }

  // getHref() {
  //   const link = this.headingRef[0].querySelector('a') as HTMLAnchorElement;
  //   if (link && link.getAttribute('href') !== null) {
  //     // @ts-expect-error
  //     this._linkHref = link.getAttribute('href');
  //   }
  // }

  // clickHandler() {    
  //   if(this._linkHref) {
  //     window.location.href = this._linkHref;
  //   }
  // }
}

/**
 * Declare the component in the global HTMLElementTagNameMap
 */
declare global {
  interface HTMLElementTagNameMap {
    [componentName]: OutlineTeaserCard;
  }
}
